import { PencilIcon, Trash2Icon, XSquareIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { deleteProperty } from "@/api/property";
import { useMutation } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { useEntityProperties } from "@/hooks/server-state/use-entity-property";
import { usePropertyModalStore } from "@/hooks/store/use-property-modal-store";

import AlertModal from "@/components/common/modals/alert-modal";
import { formInputType } from "@/components/common/modals/property-modal";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";

const PropertiesPage = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { data, isLoading } = useEntityProperties(entityId ?? "");
  const { mutate, isPending } = useMutation({
    mutationFn: (formId: number) => deleteProperty(formId),
    onSuccess: () => {
      window.location.reload();
    }
  });
  const { onOpen, setProperty, setEntityId } = usePropertyModalStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedForDelete, setSelectedForDelete] = useState<number | null>(
    null
  );
  const [searchValue, setSearchValue] = useState("");

  const filteredProperties = useMemo(() => {
    if (!data?.data) return [];
    const term = searchValue.trim().toLowerCase();
    if (!term) return data.data;

    return data.data.filter((property) => {
      const searchableFields = [
        property?.previewName ?? "",
        property?.propertyName ?? "",
        property?.description ?? "",
        property?.defaultValue ?? ""
      ];

      return searchableFields.some((field) =>
        field.toString().toLowerCase().includes(term)
      );
    });
  }, [data?.data, searchValue]);

  const DEFAULT_PAGE_SIZE = 5;
  const pageSizeParam = Number(searchParams.get("size"));
  const currentPageParam = Number(searchParams.get("page"));
  const pageSize =
    !Number.isNaN(pageSizeParam) && pageSizeParam > 0
      ? pageSizeParam
      : DEFAULT_PAGE_SIZE;
  const currentPage =
    !Number.isNaN(currentPageParam) && currentPageParam > 0
      ? currentPageParam
      : 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProperties = filteredProperties;

  useEffect(() => {
    if (searchValue.trim() && currentPage !== 1) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", "1");
        return params;
      });
    }
  }, [searchValue, currentPage, setSearchParams]);

  const showEmptyState = !paginatedProperties.length;
  const emptyStateMessage = searchValue.trim()
    ? "نتیجه‌ای مطابق جستجو یافت نشد"
    : "عنصری پیدا نشد";
  const paginationTotal = searchValue.trim()
    ? filteredProperties.length
    : data?.totalCount ?? data?.data?.length ?? 0;

  if (isLoading) return <Loading />;

  return (
    <>
      <AlertModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isPending}
        onConfirm={() => {
          mutate(selectedForDelete!);
        }}
        title="آیا از حذف عنصر اطمینان دارید؟"
        description="این عملیات قابل برگشت نخواهد بود و عنصر بصورت دائمی حذف خواهد شد"
      />

      <div className="flex flex-col gap-3 px-5 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <button
          className="flex items-center gap-x-2 text-base font-semibold hover:text-primary"
          onClick={() => {
            onOpen();
            setProperty(null);
            setEntityId(+entityId!);
          }}
        >
          <XSquareIcon size={16} className="text-primary" />
          ساخت عنصر جدید
        </button>
        <div className="w-full sm:w-60">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="جستجو بر اساس نام یا توضیحات"
          />
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ردیف</TableHead>
            <TableHead>نام نمایشی</TableHead>
            <TableHead>نام عنصر</TableHead>
            <TableHead>توضیحات</TableHead>
            <TableHead>نوع</TableHead>
            <TableHead>مقدار پیش فرض</TableHead>
            <TableHead>ویرایش</TableHead>
            <TableHead>حذف</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!!paginatedProperties.length &&
            paginatedProperties.map((property, index) => (
              <TableRow key={property.id}>
                <TableCell>{startIndex + index + 1}</TableCell>
                <TableCell>{property?.previewName}</TableCell>
                <TableCell>{property?.propertyName}</TableCell>
                <TableCell>
                  <p className="line-clamp-1">{property?.description}</p>
                </TableCell>
                <TableCell>
                  {formInputType[property?.type as keyof typeof formInputType]
                    ?.type ?? ""}
                </TableCell>
                <TableCell>{property?.defaultValue}</TableCell>
                <TableCell>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setProperty(property);
                      onOpen();
                    }}
                  >
                    <PencilIcon />
                  </button>
                </TableCell>
                <TableCell>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedForDelete(property.id);
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    <Trash2Icon className="text-red-600" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {showEmptyState && <EmptyState message={emptyStateMessage} />}
      <TablePagination totalItems={paginationTotal} />
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = ({ message = "عنصری پیدا نشد" }: { message?: string }) => (
  <div className="flex h-32 w-full items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">{message}</p>
  </div>
);

export default PropertiesPage;
